import React from "react";
import { Box, Link, Stack, Typography } from "@mui/material";
import { fetchCapacityAddressFilterOptions, fetchCapacityCityFilterOptions, fetchCapacityOwnerFilterOptions, fetchCapacityRegionFilterOptions, fetchCapacityStoreNameFilterOptions, fetchCapacityZipCodeFilterOptions, fetchStoreAvailability, listStoreCapacities, selectAllStores, manageStoreCapacityPaginationCookie } from "../../../../api_calls/loopCampaign";
import { useDispatch, useSelector } from "react-redux";
import { ArrowDropDown, ArrowDropUp, Check, Close, Download, Help, Info } from "@mui/icons-material";
import { HtmlTooltip, IconContainer, SpaceBetweenStack, StyledCheckbox, StyledDarkRedInformationTypography, StyledWhiteCheckbox } from "../../../../components/commonComponents/StyledComponents/styled";
import NumberedModalPagination from "../../../../components/commonComponents/LoadMore/NumberedModalPagination";
import { updateStateData } from "../../../../Store/actions";
import StoreAvailabilityPopup from "./StoreAvailabilityPopup";
import StoreCapacityFilter from "./StoreCapacityFilter";
import {
    canViewStoreOwner,
    checkUserDetailsFromCookie,
} from "../../../../api_calls/userManagement";
import { loadRetailerAccessAvailability } from "../../../../api_calls/retailerSelection";
import * as xlsx from "xlsx";
import { TextButton } from "../../../../components/commonComponents/Buttons/ActionButton";
import { checkTodayDateWithFormat, SortIcon } from "../../../../api_calls/utils";
import { useListingTableControllerModal } from "../../../../hooks/useListingTableControllerModal";
import {
    ListingCellLeft,
    ListingCellCenter,
    ListingHeaderContent,
    LISTING_ACTION_TO_TABLE_GAP,
} from "../../../../components/commonComponents/TabularLists/styles";
import ListingDataTableForModal from "../../../../components/commonComponents/TabularLists/ListingDataTableForModal";
import { setBatchLoadingStatus } from "../../../../api_calls/requestConfig";

const StoresList = ({ loopinfo, wizardRef }) => {
    const capacityDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const [showStoreInfoModal, setShowStoreInfoModal] = React.useState(false);
    const tableContainerRef = wizardRef; // Ref for the table container to enable scroll control from the listing controller hook
    const loopId = typeof loopinfo !== "undefined" && typeof loopinfo.id !== "undefined" ? loopinfo.id : "";

    // Retailer visibility still needs the user type, while Owner visibility uses the shared helper.
    const checkUserDetails = checkUserDetailsFromCookie();
    const userDetailObj =
        checkUserDetails.success === true ? checkUserDetails.user_detail : {};

    const [sortConfig, setSortConfig] = React.useState({
        name: "asc",
        address: "asc",
        zipcode: "asc",
        city: "asc",
        region: "asc",
        owner: "asc",
        retailer: "asc",
        available: "asc"
    });
    // Same visibility rule as bottom-nav / detail store modal retailer UI
    const [showRetailerColumn, setShowRetailerColumn] = React.useState(false);

    const loggedInUserType =
        typeof userDetailObj.user_type !== "undefined"
            ? userDetailObj.user_type.toLowerCase()
            : "";

    React.useEffect(() => {
        if (loggedInUserType === "") {
            setShowRetailerColumn(false);
            return;
        }

        let shouldApplyResult = true;

        loadRetailerAccessAvailability(loggedInUserType)
            .then((retailerAccessInfo) => {
                if (!shouldApplyResult) {
                    return;
                }
                setShowRetailerColumn(retailerAccessInfo.available === true);
            })
            .catch(() => {
                if (!shouldApplyResult) {
                    return;
                }
                setShowRetailerColumn(false);
            });

        return () => {
            shouldApplyResult = false;
        };
    }, [loggedInUserType]);

    const manageSelectAll = (selectAll) => {
        const selectedLoopData = typeof capacityDetails.new_loop_data !== "undefined" ? capacityDetails.new_loop_data : {};

        if (selectAll === true) {
            selectAllStores(loopId);
        } else {
            dispatchAction(
                updateStateData({
                    new_loop_data: {
                        ...selectedLoopData,
                        all_stores_selected: selectAll,
                        selected_stores: []
                    },
                })
            );
        }
    }

    const setStoreSelections = (selectedStoreId, companyTreePath) => {
        const selectedLoopData = typeof capacityDetails.new_loop_data !== "undefined" ? capacityDetails.new_loop_data : {};
        const selectedStores = typeof capacityDetails.new_loop_data !== "undefined" && typeof capacityDetails.new_loop_data.selected_stores !== "undefined" ? capacityDetails.new_loop_data.selected_stores : [];
        dispatchAction(
            updateStateData({
                new_loop_data: {
                    ...selectedLoopData,
                    selected_stores: [
                        ...selectedStores,
                        { number_store_cyreen: selectedStoreId, tree_path: companyTreePath }
                    ]
                },
            })
        );
    }

    const removeSelection = (selectedStoreId) => {
        const selectedLoopData = typeof capacityDetails.new_loop_data !== "undefined" ? capacityDetails.new_loop_data : {};
        const filteredStores = typeof capacityDetails.new_loop_data !== "undefined" && typeof capacityDetails.new_loop_data.selected_stores !== "undefined" ? capacityDetails.new_loop_data.selected_stores.filter(store => {
            return store.number_store_cyreen !== selectedStoreId
        }) : [];
        dispatchAction(
            updateStateData({
                new_loop_data: {
                    ...selectedLoopData,
                    all_stores_selected: false,
                    selected_stores: [...filteredStores]
                },
            })
        );
    }

    // fetch availability details for specific stores
    const storeSpecificAvailability = async (selectedStore) => {
        const fetchData = await fetchStoreAvailability(selectedStore.number_store_cyreen);
        if (fetchData.success === true) {
            setShowStoreInfoModal(true);
        }
    }

    const initStoreCapacityLookups = React.useCallback(async () => {
        setBatchLoadingStatus(true)
        await Promise.all([
            fetchCapacityStoreNameFilterOptions(),
            fetchCapacityAddressFilterOptions(),
            fetchCapacityZipCodeFilterOptions(),
            fetchCapacityCityFilterOptions(),
            fetchCapacityRegionFilterOptions(),
            fetchCapacityOwnerFilterOptions(),
        ]).then(() => setBatchLoadingStatus(false));
    }, []);


    const { pagination, onPageChange, onPageSizeChange } = useListingTableControllerModal({
        paginationReduxKey: "store_capacity_pagination",
        loadPage: (pageNumber, pageSize) => {
            return listStoreCapacities(loopId, pageNumber, pageSize);
        },
        onInit: initStoreCapacityLookups,
        scrollContainerRef: tableContainerRef,
    });

    // sorted details 
    const loopCapacityList = typeof capacityDetails.store_capacity_list !== "undefined" ? capacityDetails.store_capacity_list : [];

    const exportToExcel = async () => {
        // loop campaign name for download file name
        const loopCampaignName = typeof capacityDetails.new_loop_data !== "undefined" && typeof capacityDetails.new_loop_data.loop_name !== "undefined" ? capacityDetails.new_loop_data.loop_name.replaceAll(" ", "_") : "LoopCampaign";

        // load store list
        const storeCapacityList = await listStoreCapacities(loopId, 0, 5000, "download");
        // Convert JSON data to an array of arrays
        const data = [];

        // Build headers — Retailer only when multi-retailer access UI is shown
        const headers = [
            "Store Name",
            "Street & No.",
            "ZIP Code",
            "City",
            "Region",
            "Owner",
        ];
        if (showRetailerColumn) {
            headers.push("Retailer");
        }
        headers.push("Available");
        data.push(headers);

        // Loop through the store capacity list and add rows
        storeCapacityList.store_capacity_list.forEach((item) => {
            const row = [
                item.name,
                item.address,
                item.zipCode,
                item.city,
                item.region.name,
                typeof item.company !== "undefined" ? item.company.name : "",
            ];
            if (showRetailerColumn) {
                row.push(item.root_retailer_company?.name || "");
            }
            row.push(item.available ? "Yes" : "No");
            data.push(row);
        });

        // Create a new workbook and add a worksheet
        const workbook = xlsx.utils.book_new();
        const worksheet = xlsx.utils.aoa_to_sheet(data);

        // Add the worksheet to the workbook
        xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        // Define the export file name
        const currentDate = checkTodayDateWithFormat("YYYYMMDD");
        let exportFileName = currentDate + "_" + loopCampaignName + "_StoreOverview.xlsx";

        // Save the workbook to an Excel file
        xlsx.writeFile(workbook, exportFileName);
    };

    // remove stores that are not available anymore
    const removeUnavailableStores = async () => {
        // load store list
        const storeCapacityList = await listStoreCapacities(loopId, 0, 5000, "download", false);

        if (typeof capacityDetails.new_loop_data !== "undefined" && typeof capacityDetails.new_loop_data.selected_stores !== "undefined" && capacityDetails.new_loop_data.selected_stores.length > 0) {
            const selectedLoopData = typeof capacityDetails.new_loop_data !== "undefined" ? capacityDetails.new_loop_data : {};
            const selectedStores = capacityDetails.new_loop_data.selected_stores;

            const filteredStores = selectedStores.filter(store => {
                const storeDetails = storeCapacityList.store_capacity_list.find(item => item.number_store_cyreen === store.number_store_cyreen);
                return storeDetails && storeDetails.available === true;
            });

            dispatchAction(
                updateStateData({
                    new_loop_data: {
                        ...selectedLoopData,
                        selected_stores: [...filteredStores],
                        all_stores_selected: filteredStores.length === loopCapacityList.length
                    },
                })
            );
        }
    }

    // pre-select stores from clone source that are still available
    const preSelectCloneStores = async () => {
        const fullList = await listStoreCapacities("", 0, 5000, "download", false);
        if (!fullList?.store_capacity_list) return;

        const sourceStores = capacityDetails.new_loop_data.clone_source_stores;
        const selectedLoopData = typeof capacityDetails.new_loop_data !== "undefined" ? capacityDetails.new_loop_data : {};

        const availableStores = sourceStores.filter(srcStore => {
            const found = fullList.store_capacity_list.find(
                cap => cap.number_store_cyreen === srcStore.number_store_cyreen
            );
            return found && found.available === true;
        });

        dispatchAction(
            updateStateData({
                new_loop_data: {
                    ...selectedLoopData,
                    selected_stores: availableStores,
                    clone_source_stores: undefined,
                    all_stores_selected: false,
                },
            })
        );
    };

    const handleSortingParamChange = (sortField, sortDir) => {
        const sortOrder = sortDir === "asc" ? "desc" : "asc";
        setSortConfig((prev) => ({
            ...prev,
            [sortField]: sortOrder,
        }));

        dispatchAction(
            updateStateData({
                loop_stores_sorting_measure: {
                    sortField: sortField,
                    sortOrder: sortOrder,
                },
            })
        );

        const pageSize = pagination?.pageSize ?? 10;
        listStoreCapacities(loopId, 0, pageSize);
    };

    // Same rule as the Owner filter in StoreCapacityFilter
    const showOwnerColumn = canViewStoreOwner();

    const columns = React.useMemo(() => {
        const cols = [
            // Select checkbox column
            {
                id: "select",
                headerAlign: "center",
                align: "center",
                header: (
                    <ListingCellCenter>
                        <StyledWhiteCheckbox
                            size="small"
                            checked={
                                typeof capacityDetails?.new_loop_data?.all_stores_selected !== "undefined"
                                    ? capacityDetails.new_loop_data.all_stores_selected
                                    : typeof capacityDetails?.new_loop_data?.selected_stores !== "undefined" &&
                                        capacityDetails.new_loop_data.selected_stores.length ===
                                        capacityDetails?.store_capacity_pagination?.availableStores
                                        ? true
                                        : false
                            }
                            onChange={(e) => manageSelectAll(e.target.checked)}
                        />
                    </ListingCellCenter>
                ),
                render: (store) => (
                    <ListingCellCenter>
                        <StyledCheckbox
                            size="small"
                            disabled={store.available === false}
                            checked={
                                typeof capacityDetails?.new_loop_data?.selected_stores !== "undefined"
                                    ? capacityDetails.new_loop_data.selected_stores.some(
                                        (s) => s.number_store_cyreen === store.number_store_cyreen
                                    )
                                    : false
                            }
                            onChange={(e) => {
                                if (e.target.checked) {
                                    setStoreSelections(store.number_store_cyreen, store.company.tree_path);
                                } else {
                                    removeSelection(store.number_store_cyreen);
                                }
                            }}
                        />
                    </ListingCellCenter>
                ),
            },

            // Store Name
            {
                id: "name",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("name", sortConfig.name)} style={{ cursor: "pointer" }}>
                        Store Name
                        <SortIcon direction={sortConfig.name} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>
                        {store.available === false ? (
                            <Link
                                href="#"
                                underline="none"
                                title={store.name}
                                sx={{ color: "#AF3241" }}
                                onClick={(e) => {
                                    e.preventDefault();
                                    storeSpecificAvailability(store);
                                }}
                            >
                                {store.name}
                            </Link>
                        ) : (
                            store.name
                        )}
                    </ListingCellLeft>
                ),
            },

            // Address
            {
                id: "address",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("address", sortConfig.address)} style={{ cursor: "pointer" }}>
                        Street &amp; No.
                        <SortIcon direction={sortConfig.address} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.address}</ListingCellLeft>,
            },

            // Zip Code
            {
                id: "zipCode",
                cellSx: { width: "148px" },
                headerSx: { width: "148px" },
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("zipcode", sortConfig.zipcode)} style={{ cursor: "pointer" }}>
                        ZIP Code
                        <SortIcon direction={sortConfig.zipcode} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.zipCode}</ListingCellLeft>,
            },

            // City
            {
                id: "city",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("city", sortConfig.city)} style={{ cursor: "pointer" }}>
                        City
                        <SortIcon direction={sortConfig.city} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list",
                render: (store) => <ListingCellLeft>{store.city}</ListingCellLeft>,
            },

            //Region
            {
                id: "region",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("region", sortConfig.region)} style={{ cursor: "pointer" }}>
                        Region
                        <SortIcon direction={sortConfig.region} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => <ListingCellLeft>{store.region?.name}</ListingCellLeft>,
            },
        ];

        //Conditional owner column
        if (showOwnerColumn) {
            cols.push({
                id: "company",
                header: (
                    <ListingHeaderContent onClick={() => handleSortingParamChange("owner", sortConfig.owner)} style={{ cursor: "pointer" }}>
                        Owner
                        <SortIcon direction={sortConfig.owner} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => <ListingCellLeft>{store.company?.name}</ListingCellLeft>,
            });
        }

        // Root retailer — only for users who see the bottom-nav retailer dropdown
        if (showRetailerColumn) {
            cols.push({
                id: "retailer",
                header: (
                    <ListingHeaderContent
                        onClick={() => handleSortingParamChange("retailer", sortConfig.retailer)}
                        style={{ cursor: "pointer" }}
                    >
                        Retailer
                        <SortIcon direction={sortConfig.retailer} />
                    </ListingHeaderContent>
                ),
                cellClassName: "text-wrapper-store_list wrapLargeText",
                render: (store) => (
                    <ListingCellLeft>{store.root_retailer_company?.name || ""}</ListingCellLeft>
                ),
            });
        }

        //Available column
        cols.push({
            id: "available",
            headerAlign: "center",
            align: "center",
            header: (
                <ListingHeaderContent onClick={() => handleSortingParamChange("available", sortConfig.available)} style={{ cursor: "pointer" }}>
                    Available
                    <SortIcon direction={sortConfig.available} />
                </ListingHeaderContent>
            ),
            render: (store) => (
                <ListingCellCenter>
                    {store.available === true ? (
                        <Check sx={{ color: "#AF3241" }} />
                    ) : (
                        <Close sx={{ color: "#E6E6E6" }} />
                    )}
                </ListingCellCenter>
            ),
        });

        return cols;
    }, [
        sortConfig,
        capacityDetails?.new_loop_data,
        showOwnerColumn,
        showRetailerColumn,
    ]);

    const didCleanupRef = React.useRef(false);
    React.useEffect(() => {
        if (!capacityDetails?.store_capacity_list) return;
        if (didCleanupRef.current) return;

        // existing campaigns: remove stores that became unavailable
        if (loopId) {
            didCleanupRef.current = true;
            removeUnavailableStores();
            return;
        }

        // clone mode: pre-select available stores from source
        if (capacityDetails?.new_loop_data?.clone_source_stores?.length > 0) {
            didCleanupRef.current = true;
            preSelectCloneStores();
        }
    }, [loopId, capacityDetails?.store_capacity_list, capacityDetails?.new_loop_data?.clone_source_stores]);

    return (
        <>
            <Typography variant="body2">
                Please select all stores where the visuals should be played.
            </Typography>
            <Box height={5} />
            <Typography variant="body2">
                Each store displays an indicator showing whether it is available for your selected playout schedule. You may click on the name of any unavailable store to view more information about why it cannot be used for playout.
            </Typography>
            <Box height={20} />
            {/* ---------------- filters ------------ */}
            {
                typeof capacityDetails.capacity_address_names_list !== "undefined" && typeof capacityDetails.capacity_zip_code_list !== "undefined" && typeof capacityDetails.capacity_region_names_list !== "undefined" && typeof capacityDetails.capacity_city_names_list !== "undefined" && (
                    <StoreCapacityFilter loopId={loopId} />
                )
            }

            {typeof capacityDetails.capacity_address_names_list !== "undefined" &&
                typeof capacityDetails.capacity_zip_code_list !== "undefined" &&
                typeof capacityDetails.capacity_region_names_list !== "undefined" &&
                typeof capacityDetails.capacity_city_names_list !== "undefined" && (
                    <Box height={LISTING_ACTION_TO_TABLE_GAP} />
                )}

            {/* ------------ stores availability listing ---------- */}
            {typeof capacityDetails.store_capacity_list !== "undefined" && (
                <ListingDataTableForModal
                    columns={columns}
                    rows={loopCapacityList}
                    rowKey={(store, idx) => `${store.number_store_cyreen ?? idx}_store_capacity`}
                    emptyText="No results"
                    // Don’t use the normal footer in a wizard modal:
                    pagination={null}
                    onPageChange={null}
                    onPageSizeChange={null}
                />
            )}
            {/* ----------- selected stores count ------------- */}
            {
                typeof capacityDetails.store_capacity_pagination !== "undefined" && (
                    <>
                        {/* <Box height={5} /> */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
                            <StyledDarkRedInformationTypography variant="body2">
                                Selected {typeof capacityDetails.new_loop_data !== "undefined" && typeof capacityDetails.new_loop_data.selected_stores !== "undefined" ? capacityDetails.new_loop_data.selected_stores.length : 0} of {capacityDetails.store_capacity_pagination.availableStores}
                            </StyledDarkRedInformationTypography>

                            <Stack direction="row" alignItems="center" spacing={-1}>
                                <TextButton
                                    onClick={(e) => {
                                        e.preventDefault();
                                        exportToExcel();
                                    }}
                                    label={"Download Excel"}
                                    icon={<Download />}
                                    sx={{
                                        mt: 2
                                    }}
                                />
                                <HtmlTooltip
                                    title={
                                        <Box sx={{ maxWidth: 200 }}>
                                            <Typography variant="body2">
                                                The export reflects the current state of data at the time of download. Data in CAP Explorer may change over time.
                                            </Typography>
                                        </Box>
                                    }
                                >
                                    <IconContainer>
                                        <Info fontSize="small" sx={{ marginTop: "8px" }} />
                                    </IconContainer>
                                </HtmlTooltip>
                            </Stack>
                        </Stack>
                    </>
                )
            }

            {/* --------- pagination --------- */}
            {
                typeof capacityDetails.store_capacity_pagination !== "undefined" && (
                    <NumberedModalPagination
                        totalCount={capacityDetails.store_capacity_pagination.totalPages}
                        totalRecordCount={capacityDetails.store_capacity_pagination.totalRecords}
                        defaultSelectedPage={capacityDetails.store_capacity_pagination.pageNumber + 1}
                        changeCall={onPageChange}
                        rowSizeChangeCall={onPageSizeChange}
                        rowSize={capacityDetails.store_capacity_pagination.pageSize}
                    />

                )
            }
            {/* ------- store availability popup ------- */}
            {
                showStoreInfoModal && (
                    <StoreAvailabilityPopup
                        open={showStoreInfoModal}
                        closeModal={() => {
                            setShowStoreInfoModal(false);
                        }}
                    />
                )
            }
        </>
    );
};

export default StoresList;
